/**
 * HarborWell Connect — Phase 5 Documentation Governance & Version Center.
 *
 * This module is presented inside ArchitectureDocs.jsx as a set of
 * governance panels (Dashboard, Timeline, Compare, Search, Integrity).
 * Every panel is admin-scoped and read-only apart from Restore + Verify.
 */
import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatRelative } from "@/lib/api";
import { toast } from "sonner";
import {
  Activity, ArrowRightLeft, RotateCcw, ShieldCheck, Search, Loader2,
  Sparkles, TrendingUp, TrendingDown, Minus, ClipboardList, HardDriveDownload,
} from "lucide-react";

function formatBytes(n) {
  if (!Number.isFinite(n)) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function readinessTone(band) {
  switch (band) {
    case "excellent":    return "text-[#166534] bg-[#DCFCE7]";
    case "strong":       return "text-[#1E3A8A] bg-[#DBEAFE]";
    case "acceptable":   return "text-[#92400E] bg-[#FEF3C7]";
    case "at_risk":      return "text-[#B45309] bg-[#FEE2E2]";
    default:              return "text-[#991B1B] bg-[#FEE2E2]";
  }
}

function verdictTone(v) {
  if (v === "improvement") return "text-[#166534] bg-[#DCFCE7]";
  if (v === "regression")  return "text-[#991B1B] bg-[#FEE2E2]";
  return "text-slate-600 bg-slate-100";
}

// -----------------------------------------------------------------------------
// Governance Dashboard
// -----------------------------------------------------------------------------
export function GovernanceDashboard({ dashboard, readiness, onRefresh }) {
  if (!dashboard) return null;
  const { latest, previous, warnings_summary, coverage_trend } = dashboard;
  const covPoints = coverage_trend || [];
  const covMax = Math.max(100, ...covPoints.map((p) => p.coverage_pct));
  const covMin = Math.min(...covPoints.map((p) => p.coverage_pct), 0);

  const delta = latest && previous
    ? Math.round(((latest.coverage_pct || 0) - (previous.coverage_pct || 0)) * 100) / 100
    : null;
  const TrendIcon = delta == null || delta === 0 ? Minus : delta > 0 ? TrendingUp : TrendingDown;

  return (
    <section
      data-testid="arch-docs-governance-dashboard"
      className="rounded-2xl border border-[#E5E5E0] bg-white p-5 space-y-4"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-[#1E3A8A] inline-flex items-center gap-2">
          <Activity className="w-5 h-5" /> Governance Dashboard
        </h3>
        <button
          onClick={onRefresh}
          data-testid="arch-docs-governance-refresh"
          className="text-xs text-[#1E3A8A] hover:underline"
        >
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl bg-slate-50 px-3 py-2" data-testid="arch-docs-gov-latest">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Latest version</div>
          <div className="text-lg font-semibold text-[#1E3A8A]">{latest?.version_number || "—"}</div>
          <div className="text-[11px] text-slate-500">{latest?.generated_at ? formatRelative(latest.generated_at) : ""}</div>
        </div>
        <div className="rounded-xl bg-slate-50 px-3 py-2" data-testid="arch-docs-gov-previous">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Previous version</div>
          <div className="text-lg font-semibold text-[#1E3A8A]">{previous?.version_number || "—"}</div>
          <div className="text-[11px] text-slate-500">{previous?.generated_at ? formatRelative(previous.generated_at) : ""}</div>
        </div>
        <div className="rounded-xl bg-slate-50 px-3 py-2" data-testid="arch-docs-gov-coverage">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Coverage · trend</div>
          <div className="text-lg font-semibold text-[#1E3A8A] flex items-center gap-1">
            {(latest?.coverage_pct || 0).toFixed(2)}% <TrendIcon className="w-4 h-4" />
          </div>
          <div className="text-[11px] text-slate-500">
            {delta == null ? "—" : `${delta > 0 ? "+" : ""}${delta.toFixed(2)} pp vs previous`}
          </div>
        </div>
        <div className="rounded-xl bg-slate-50 px-3 py-2" data-testid="arch-docs-gov-warnings">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Warnings</div>
          <div className="text-lg font-semibold text-[#1E3A8A]">{warnings_summary?.total ?? 0}</div>
          <div className="text-[11px] text-slate-500">
            {warnings_summary?.outstanding_manual ?? 0} outstanding manual · {warnings_summary?.discovery_errors ?? 0} discovery errors
          </div>
        </div>
      </div>

      {/* Readiness score */}
      {readiness && (
        <div className="rounded-xl border border-[#E5E5E0] p-3" data-testid="arch-docs-readiness">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Release Readiness · {readiness.version_number}
            </div>
            <span className={`text-xs uppercase tracking-wider px-2 py-0.5 rounded-full font-medium ${readinessTone(readiness.band)}`}>
              {readiness.band.replace(/_/g, " ")} · {readiness.overall_readiness.toFixed(1)}%
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            {[
              ["Coverage",                readiness.coverage_score,            "arch-docs-readiness-coverage"],
              ["Validation",              readiness.validation_score,          "arch-docs-readiness-validation"],
              ["Export completeness",     readiness.export_completeness,       "arch-docs-readiness-export"],
              ["Architecture completeness", readiness.architecture_completeness, "arch-docs-readiness-arch"],
            ].map(([label, val, tid]) => (
              <div key={label} data-testid={tid} className="rounded-lg bg-slate-50 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
                <div className="text-base font-semibold text-[#1E3A8A]">{Number(val).toFixed(1)}%</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Coverage trend sparkline */}
      {covPoints.length > 1 && (
        <div className="rounded-xl border border-[#E5E5E0] p-3" data-testid="arch-docs-coverage-trend">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Coverage trend (last {covPoints.length})</div>
          <svg viewBox={`0 0 ${covPoints.length * 30} 60`} className="w-full h-16">
            <polyline
              fill="none" stroke="#1E3A8A" strokeWidth="2"
              points={covPoints.map((p, i) => {
                const x = i * 30 + 15;
                const range = Math.max(1, covMax - covMin);
                const y = 55 - ((p.coverage_pct - covMin) / range) * 45;
                return `${x},${y}`;
              }).join(" ")}
            />
            {covPoints.map((p, i) => {
              const x = i * 30 + 15;
              const range = Math.max(1, covMax - covMin);
              const y = 55 - ((p.coverage_pct - covMin) / range) * 45;
              return <circle key={p.version_id} cx={x} cy={y} r="3" fill="#1E3A8A" />;
            })}
          </svg>
        </div>
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
// Timeline
// -----------------------------------------------------------------------------
export function DocumentationTimeline({ rows, onOpen, onCompare, onRestore, activeVersionId }) {
  if (!rows) return null;
  return (
    <section
      data-testid="arch-docs-timeline"
      className="rounded-2xl border border-[#E5E5E0] bg-white p-5"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-[#1E3A8A] inline-flex items-center gap-2">
          <ClipboardList className="w-5 h-5" /> Documentation Timeline
        </h3>
        <span className="text-xs text-slate-500">{rows.length} versions</span>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-slate-500">No versions yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="arch-docs-timeline-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                <th className="text-left px-2 py-1.5">Version</th>
                <th className="text-left px-2 py-1.5">Build</th>
                <th className="text-left px-2 py-1.5">Generated</th>
                <th className="text-left px-2 py-1.5">By</th>
                <th className="text-right px-2 py-1.5">Coverage</th>
                <th className="text-right px-2 py-1.5">Warn.</th>
                <th className="text-right px-2 py-1.5">Exports</th>
                <th className="text-right px-2 py-1.5">Downloads</th>
                <th className="text-right px-2 py-1.5">Total size</th>
                <th className="text-right px-2 py-1.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((v) => (
                <tr key={v.id}
                    className={`border-t border-[#E5E5E0] ${v.id === activeVersionId ? "bg-[#F0F9FF]" : ""}`}
                    data-testid={`arch-docs-timeline-row-${v.id}`}>
                  <td className="px-2 py-1.5 font-medium">{v.version_number}
                    {v.id === activeVersionId && (
                      <span className="ml-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#DCFCE7] text-[#166534]">
                        active
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 font-mono text-[11px] text-slate-600">{v.build_number}</td>
                  <td className="px-2 py-1.5 text-slate-600">{formatRelative(v.generated_at)}</td>
                  <td className="px-2 py-1.5 text-slate-600 truncate max-w-[140px]">{v.generated_by || "—"}</td>
                  <td className="px-2 py-1.5 text-right">{(v.coverage_pct || 0).toFixed(1)}%</td>
                  <td className="px-2 py-1.5 text-right">{v.warning_count}</td>
                  <td className="px-2 py-1.5 text-right">{v.export_count}</td>
                  <td className="px-2 py-1.5 text-right">{v.download_count}</td>
                  <td className="px-2 py-1.5 text-right">{formatBytes(v.total_bytes)}</td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap">
                    <button onClick={() => onOpen(v.id)} className="text-[#1E3A8A] hover:underline mr-2"
                            data-testid={`arch-docs-timeline-open-${v.id}`}>Open</button>
                    <button onClick={() => onCompare(v.id)} className="text-[#1E3A8A] hover:underline mr-2"
                            data-testid={`arch-docs-timeline-compare-${v.id}`}>Compare</button>
                    <button onClick={() => onRestore(v.id)} className="text-[#1E3A8A] hover:underline"
                            data-testid={`arch-docs-timeline-restore-${v.id}`}>Restore</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
// Compare panel
// -----------------------------------------------------------------------------
export function CompareVersions({ versions, defaultA, defaultB }) {
  const [aId, setAId] = useState(defaultA || null);
  const [bId, setBId] = useState(defaultB || null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (defaultA) setAId(defaultA); }, [defaultA]);
  useEffect(() => { if (defaultB) setBId(defaultB); }, [defaultB]);

  const run = async () => {
    if (!aId || !bId) return toast.error("Choose two versions to compare.");
    if (aId === bId) return toast.error("Choose two different versions.");
    setLoading(true);
    try {
      const { data } = await api.post("/architecture-docs/comparisons", { a_id: aId, b_id: bId });
      setReport(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="arch-docs-compare-panel"
      className="rounded-2xl border border-[#E5E5E0] bg-white p-5 space-y-4"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-[#1E3A8A] inline-flex items-center gap-2">
          <ArrowRightLeft className="w-5 h-5" /> Compare Versions
        </h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <select value={aId || ""} onChange={(e) => setAId(e.target.value)}
                className="px-3 py-2 rounded-full border border-[#E5E5E0] bg-white text-sm"
                data-testid="arch-docs-compare-a">
          <option value="">Version A …</option>
          {(versions || []).map((v) => (
            <option key={v.id} value={v.id}>{v.version_number} · {v.build_number}</option>
          ))}
        </select>
        <select value={bId || ""} onChange={(e) => setBId(e.target.value)}
                className="px-3 py-2 rounded-full border border-[#E5E5E0] bg-white text-sm"
                data-testid="arch-docs-compare-b">
          <option value="">Version B …</option>
          {(versions || []).map((v) => (
            <option key={v.id} value={v.id}>{v.version_number} · {v.build_number}</option>
          ))}
        </select>
        <button onClick={run} disabled={loading}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#1E3A8A]/90 disabled:opacity-60"
                data-testid="arch-docs-compare-run">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRightLeft className="w-4 h-4" />}
          Run comparison
        </button>
      </div>

      {report && (
        <div className="space-y-3" data-testid="arch-docs-compare-report">
          {/* Executive summary */}
          <div className="rounded-xl border border-[#E5E5E0] p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wider text-slate-500">Executive Summary</div>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-medium ${verdictTone(report.executive_summary.verdict)}`}
                    data-testid="arch-docs-compare-verdict">
                {report.executive_summary.verdict}
              </span>
            </div>
            <p className="text-sm text-slate-700 mb-1">{report.executive_summary.coverage_narrative}</p>
            <p className="text-sm text-slate-700 mb-1">{report.executive_summary.total_narrative}</p>
            <p className="text-sm text-slate-700 mb-2">{report.executive_summary.warnings_narrative}</p>
            <ul className="text-xs text-slate-600 list-disc pl-5 space-y-0.5">
              {(report.executive_summary.domain_highlights || []).map((h, i) => <li key={i}>{h}</li>)}
            </ul>
          </div>

          {/* Domain diff table */}
          <div className="rounded-xl border border-[#E5E5E0] p-3">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Engineering Diff by Domain</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="arch-docs-compare-domains">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="text-left px-2 py-1">Domain</th>
                    <th className="text-right px-2 py-1">Added</th>
                    <th className="text-right px-2 py-1">Removed</th>
                    <th className="text-right px-2 py-1">Changed</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(report.domains || {}).filter(([k]) => k !== "other").map(([k, d]) => (
                    <tr key={k} className="border-t border-[#E5E5E0]" data-testid={`arch-docs-compare-domain-${k}`}>
                      <td className="px-2 py-1 font-medium text-slate-700">
                        {k}
                        {k === "diagrams" ? (
                          <span className="ml-2 px-1.5 py-0.5 rounded bg-[#EFF6FF] border border-[#BFDBFE] text-[9px] uppercase tracking-wider text-[#1E3A8A]">Phase 6</span>
                        ) : null}
                      </td>
                      <td className="px-2 py-1 text-right text-[#166534]">+{d.totals?.added || 0}</td>
                      <td className="px-2 py-1 text-right text-[#991B1B]">-{d.totals?.removed || 0}</td>
                      <td className="px-2 py-1 text-right text-[#92400E]">~{d.totals?.changed || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Section deltas — side-by-side coverage bars */}
          <div className="rounded-xl border border-[#E5E5E0] p-3">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">
              Side-by-side Section Coverage
            </div>
            <div className="grid grid-cols-1 gap-1 max-h-[440px] overflow-y-auto pr-1" data-testid="arch-docs-compare-sections">
              {(report.sections_delta || []).map((s) => (
                <div key={s.key} className="flex items-center gap-2 text-xs">
                  <span className="w-52 shrink-0 text-slate-700">{s.title}</span>
                  <div className="flex-1 flex items-center gap-1">
                    <div className="w-12 text-right text-slate-500">{s.coverage_pct_a.toFixed(0)}%</div>
                    <div className="flex-1 h-2 bg-slate-100 rounded">
                      <div className="h-2 bg-slate-400 rounded" style={{ width: `${Math.min(100, s.coverage_pct_a)}%` }} />
                    </div>
                    <div className="w-12 text-right text-slate-500">{s.coverage_pct_b.toFixed(0)}%</div>
                    <div className="flex-1 h-2 bg-slate-100 rounded">
                      <div className={`h-2 rounded ${s.coverage_delta >= 0 ? "bg-[#1E3A8A]" : "bg-[#B91C1C]"}`}
                           style={{ width: `${Math.min(100, s.coverage_pct_b)}%` }} />
                    </div>
                    <span className={`w-14 text-right ${s.coverage_delta > 0 ? "text-[#166534]" : s.coverage_delta < 0 ? "text-[#991B1B]" : "text-slate-500"}`}>
                      {s.coverage_delta > 0 ? "+" : ""}{s.coverage_delta.toFixed(1)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Warnings delta */}
          {(report.warnings_delta?.added?.length || report.warnings_delta?.removed?.length) ? (
            <div className="rounded-xl border border-[#E5E5E0] p-3" data-testid="arch-docs-compare-warnings">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Warnings Delta</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#166534] mb-1">Added ({report.warnings_delta.added.length})</div>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {report.warnings_delta.added.slice(0, 20).map((w, i) => (
                      <li key={i}><span className="uppercase text-[9px] text-[#991B1B] mr-1">{w.severity}</span>{w.title}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#991B1B] mb-1">Removed ({report.warnings_delta.removed.length})</div>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {report.warnings_delta.removed.slice(0, 20).map((w, i) => (
                      <li key={i}><span className="uppercase text-[9px] text-slate-500 mr-1">{w.severity}</span>{w.title}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
// Search
// -----------------------------------------------------------------------------
export function DocumentationSearch({ activeVersionId }) {
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("all"); // all | active
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!q.trim()) return;
    setLoading(true);
    try {
      const params = { q };
      if (scope === "active" && activeVersionId) params.version_id = activeVersionId;
      const { data } = await api.get("/architecture-docs/search", { params });
      setResults(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="arch-docs-search-panel"
      className="rounded-2xl border border-[#E5E5E0] bg-white p-5 space-y-3"
    >
      <div className="flex items-center gap-2">
        <h3 className="text-lg font-semibold text-[#1E3A8A] inline-flex items-center gap-2">
          <Search className="w-5 h-5" /> Documentation Search
        </h3>
      </div>
      <div className="flex flex-wrap gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") run(); }}
          placeholder="Search modules, APIs, collections, permissions, integrations …"
          className="flex-1 min-w-[260px] px-3 py-2 rounded-full border border-[#E5E5E0] bg-white text-sm"
          data-testid="arch-docs-search-input"
        />
        <select value={scope} onChange={(e) => setScope(e.target.value)}
                className="px-3 py-2 rounded-full border border-[#E5E5E0] bg-white text-sm"
                data-testid="arch-docs-search-scope">
          <option value="all">All versions</option>
          <option value="active">Active version only</option>
        </select>
        <button onClick={run} disabled={loading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#1E3A8A]/90 disabled:opacity-60"
                data-testid="arch-docs-search-run">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Search
        </button>
      </div>
      {results && (
        <div className="text-xs text-slate-500 mb-1" data-testid="arch-docs-search-summary">
          {results.total} section match{results.total === 1 ? "" : "es"}
          {typeof results.diagram_total === "number" ? (
            <span> · {results.diagram_total} diagram match{results.diagram_total === 1 ? "" : "es"}</span>
          ) : null}
        </div>
      )}
      {results?.diagram_results?.length ? (
        <div className="text-[10px] text-slate-500 leading-relaxed mb-1"
             data-testid="arch-docs-diagram-provenance-hint">
          <span className="inline-flex items-center gap-1"><span className="px-1.5 py-0.5 rounded bg-[#ECFDF5] border border-[#A7F3D0] text-[#059669] font-semibold">AUTOMATIC</span> regenerated from code every build</span>
          {" · "}
          <span className="inline-flex items-center gap-1"><span className="px-1.5 py-0.5 rounded bg-[#FEF3C7] border border-[#FDE68A] text-[#B45309] font-semibold">PARTIAL</span> curated layout · elements auto-verified</span>
          {" · "}
          <span className="inline-flex items-center gap-1"><span className="px-1.5 py-0.5 rounded bg-[#EFF6FF] border border-[#BFDBFE] text-[#1E3A8A] font-semibold">CURATED</span> maintained by architecture governance</span>
        </div>
      ) : null}
      {results?.results?.length ? (
        <ul className="space-y-1" data-testid="arch-docs-search-results">
          {results.results.map((r, i) => (
            <li key={`${r.version_id}:${r.key}:${i}`} className="border-t border-[#E5E5E0] py-2">
              <div className="text-xs font-medium text-[#1E3A8A]">{r.title}</div>
              <div className="text-[11px] text-slate-500 font-mono">version={r.version_id.slice(0, 12)}… · key={r.key} · coverage={(r.coverage_pct || 0).toFixed(0)}%</div>
              <div className="text-xs text-slate-700 mt-1">{r.snippet}</div>
            </li>
          ))}
        </ul>
      ) : null}
      {results?.diagram_results?.length ? (
        <ul className="space-y-1 mt-2" data-testid="arch-docs-search-diagram-results">
          {results.diagram_results.map((d, i) => {
            const src = d.source_type || "curated";
            const srcColors = {
              automatic: "bg-[#ECFDF5] border-[#A7F3D0] text-[#059669]",
              partial:   "bg-[#FEF3C7] border-[#FDE68A] text-[#B45309]",
              curated:   "bg-[#EFF6FF] border-[#BFDBFE] text-[#1E3A8A]",
            }[src] || "bg-slate-100 border-slate-200 text-slate-600";
            const stale = d.freshness_status === "stale";
            const staleSev = d.freshness_sentinel_severity || "medium";
            return (
            <li key={`d:${d.version_id}:${d.diagram_id}:${i}`}
                className="border-t border-[#E5E5E0] py-2"
                data-testid={`arch-docs-search-diagram-row-${d.diagram_id}`}
                data-source-type={src}
                data-freshness-status={d.freshness_status || "unknown"}>
              <div className="text-xs font-medium text-[#1E3A8A] inline-flex items-center gap-2 flex-wrap">
                <span className="px-2 py-0.5 rounded-full bg-[#EFF6FF] border border-[#BFDBFE] text-[10px] uppercase tracking-wider text-[#1E3A8A]">Diagram</span>
                <span className={`px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider ${srcColors}`}
                      data-testid={`arch-docs-search-source-type-${d.diagram_id}`}>{src}</span>
                {stale ? (
                  <span className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold ${staleSev === "high" ? "bg-[#FEE2E2] border-[#FCA5A5] text-[#B91C1C]" : "bg-[#FEF3C7] border-[#FDE68A] text-[#B45309]"}`}
                        data-testid={`arch-docs-search-freshness-stale-${d.diagram_id}`}>⚠ stale · {staleSev}</span>
                ) : null}
                {d.diagram_version ? (
                  <span className="text-[10px] text-slate-500 font-mono">v{d.diagram_version}</span>
                ) : null}
                <span>{d.title}</span>
              </div>
              <div className="text-[11px] text-slate-500 font-mono">
                version={d.version_id.slice(0, 12)}… · id={d.diagram_id} · family={d.family || "—"} · target={d.target_section || "—"}
              </div>
              <div className="text-[11px] text-slate-500 font-mono">
                svg sha256={d.svg_sha256 ? d.svg_sha256.slice(0, 16) + "…" : "—"} · png sha256={d.png_sha256 ? d.png_sha256.slice(0, 16) + "…" : "—"}
              </div>
              {d.snippet ? <div className="text-xs text-slate-700 mt-1">{d.snippet}</div> : null}
            </li>
            );
          })}
        </ul>
      ) : null}
      {results && !results.results?.length && !results.diagram_results?.length ? (
        <div className="text-sm text-slate-500">No matches.</div>
      ) : null}
    </section>
  );
}

// -----------------------------------------------------------------------------
// Integrity Verification
// -----------------------------------------------------------------------------
export function IntegrityVerification({ versionId }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!versionId) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/architecture-docs/versions/${versionId}/verify-package`);
      setReport(data);
      if (data.all_match) toast.success(`All ${data.artifact_count} artifacts verified.`);
      else toast.error("Integrity mismatch detected. See details below.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const summary = useMemo(() => {
    if (!report) return null;
    const ok = report.checks.filter((c) => c.matches).length;
    const diagrams = report.checks.filter((c) => (c.format || "").startsWith("diagram_"));
    const diagrams_ok = diagrams.filter((c) => c.matches).length;
    return { ok, total: report.artifact_count, diagrams: diagrams.length, diagrams_ok };
  }, [report]);

  return (
    <section data-testid="arch-docs-integrity-panel"
             className="rounded-2xl border border-[#E5E5E0] bg-white p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-[#1E3A8A] inline-flex items-center gap-2">
          <ShieldCheck className="w-5 h-5" /> Integrity Verification
        </h3>
        <button onClick={run} disabled={loading || !versionId}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1E3A8A] text-white text-xs font-medium hover:bg-[#1E3A8A]/90 disabled:opacity-60"
                data-testid="arch-docs-verify-package-btn">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <HardDriveDownload className="w-3.5 h-3.5" />}
          Verify package
        </button>
      </div>
      {summary && (
        <div className="text-xs text-slate-500" data-testid="arch-docs-integrity-summary">
          {summary.ok}/{summary.total} artifacts match stored SHA-256.
          {summary.diagrams > 0 ? (
            <span data-testid="arch-docs-integrity-diagram-summary">
              {" "}Includes {summary.diagrams_ok}/{summary.diagrams} diagram asset{summary.diagrams === 1 ? "" : "s"}.
              <span className="text-[10px] text-slate-500 block mt-1"
                    data-testid="arch-docs-integrity-provenance-hint">
                Diagram provenance: <span className="px-1.5 py-0.5 rounded bg-[#ECFDF5] border border-[#A7F3D0] text-[#059669] font-semibold">AUTOMATIC</span> regenerated from code every build; <span className="px-1.5 py-0.5 rounded bg-[#FEF3C7] border border-[#FDE68A] text-[#B45309] font-semibold">PARTIAL</span> curated layout · elements auto-verified; <span className="px-1.5 py-0.5 rounded bg-[#EFF6FF] border border-[#BFDBFE] text-[#1E3A8A] font-semibold">CURATED</span> maintained by architecture governance.
              </span>
            </span>
          ) : null}
          {report.all_match ? " All good." : " Mismatches present."}
        </div>
      )}
      {report && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="arch-docs-integrity-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                <th className="text-left px-2 py-1">Format</th>
                <th className="text-left px-2 py-1">Filename</th>
                <th className="text-right px-2 py-1">Size</th>
                <th className="text-left px-2 py-1">Match</th>
              </tr>
            </thead>
            <tbody>
              {report.checks.map((c) => {
                const isDiagram = (c.format || "").startsWith("diagram_");
                return (
                <tr key={c.format} className="border-t border-[#E5E5E0]"
                    data-testid={`arch-docs-integrity-row-${c.format}`}
                    data-kind={isDiagram ? "diagram" : "artifact"}>
                  <td className="px-2 py-1 font-medium">
                    {c.format}
                    {isDiagram ? (
                      <span className="ml-2 px-1.5 py-0.5 rounded bg-[#EFF6FF] border border-[#BFDBFE] text-[9px] uppercase tracking-wider text-[#1E3A8A]"
                            data-testid={`arch-docs-integrity-diagram-badge-${c.format}`}>Diagram</span>
                    ) : null}
                  </td>
                  <td className="px-2 py-1 font-mono text-[11px] text-slate-600">{c.filename || "—"}</td>
                  <td className="px-2 py-1 text-right">{formatBytes(c.stored_size_bytes || c.computed_size_bytes || 0)}</td>
                  <td className={`px-2 py-1 font-medium ${c.matches ? "text-[#166534]" : "text-[#991B1B]"}`}>
                    {c.matches ? "✓ match" : "✗ mismatch"}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
// Restore button (used inline in the timeline row action)
// -----------------------------------------------------------------------------
export async function restoreVersion(versionId) {
  await api.post(`/architecture-docs/versions/${versionId}/restore`);
}

// Named export for the icon set (used by the parent page tab).
export const VersionCenterIcons = { RotateCcw };
